import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarClock, ClipboardCheck, Copy, Layers, Megaphone, Settings, Sparkles, Trophy, Users } from "lucide-react";
import { toast } from "sonner";

import { ChalkCard, DarkHeader } from "../components/chalk/primitives";
import BracketTab from "../components/tournament/BracketTab";
import CheckinTab from "../components/tournament/CheckinTab";
import DivisionsTab from "../components/tournament/DivisionsTab";
import NewsTab from "../components/tournament/NewsTab";
import ScheduleTab from "../components/tournament/ScheduleTab";
import SettingsTab from "../components/tournament/SettingsTab";
import ShowcaseTab from "../components/tournament/ShowcaseTab";
import TeamsTab from "../components/tournament/TeamsTab";
import { DivisionPicker, HubGate } from "../components/tournament/shared";
import { copyToClipboard } from "../lib/copyToClipboard";
import { tournaments } from "../lib/tournamentApi";
import { sportLabel } from "../lib/sports";

const TABS = [
  { k: "divisions", label: "Divisions", icon: Layers, hub: true, director: true },
  { k: "teams", label: "Teams", icon: Users },
  { k: "bracket", label: "Bracket", icon: Trophy },
  { k: "schedule", label: "Schedule", icon: CalendarClock, hub: true },
  { k: "checkin", label: "Check-in", icon: ClipboardCheck, hub: true },
  { k: "news", label: "News", icon: Megaphone, hub: true },
  { k: "showcase", label: "Showcase", icon: Sparkles, director: true },
  { k: "settings", label: "Settings", icon: Settings, director: true },
];
const GATE_LABEL = { divisions: "Multiple divisions", schedule: "Schedule by location", checkin: "Check-in", news: "Announcements" };

export default function TournamentWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("teams");
  const [divisionId, setDivisionId] = useState(null);

  async function reload() {
    try { setData(await tournaments.get(id)); }
    catch (e) { toast.error(e.message || "Couldn't load."); }
  }
  useEffect(() => { reload(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).get("upgraded")) return undefined;
    toast.success("Hub is on for this tournament.");
    const tm = setTimeout(reload, 3000);
    return () => clearTimeout(tm);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null;
  const t = data.tournament;
  const isDirector = data.my_role === "director";
  const hub = t.tier === "hub";
  const division = data.divisions.find((d) => d.id === divisionId) || data.divisions[0];
  const tabs = TABS.filter((x) => !x.director || isDirector);
  const props = { data, division, isDirector, onChanged: reload };
  const gated = TABS.find((x) => x.k === tab)?.hub && !hub;

  return (
    <>
      <DarkHeader title={t.name} subtitle={`${sportLabel(t.sport_key, t.sport_custom)} · ${t.team_type} · ${t.status}`}
                  right={<BackBtn onClick={() => nav("/app/tournaments")} />} />
      <div style={{ padding: "12px 18px 100px" }} data-testid={`tournament-${t.id}`}>
        <ShareCard url={`${window.location.origin}/t/${t.slug}`} />
        <TabBar tabs={tabs} tab={tab} setTab={setTab} hub={hub} />
        {["teams", "bracket", "checkin"].includes(tab) && !gated && (
          <DivisionPicker divisions={data.divisions} value={division.id} onChange={setDivisionId} />
        )}
        {gated ? <HubGate data={data} feature={GATE_LABEL[tab]} onChanged={reload} /> : (
          <>
            {tab === "divisions" && <DivisionsTab {...props} />}
            {tab === "teams" && <TeamsTab {...props} />}
            {tab === "bracket" && <BracketTab {...props} />}
            {tab === "schedule" && <ScheduleTab {...props} />}
            {tab === "checkin" && <CheckinTab {...props} />}
            {tab === "news" && <NewsTab {...props} />}
            {tab === "showcase" && <ShowcaseTab {...props} />}
            {tab === "settings" && <SettingsTab {...props} />}
          </>
        )}
      </div>
    </>
  );
}

function BackBtn({ onClick }) {
  return (
    <button onClick={onClick} aria-label="Back to tournaments" data-testid="tournament-back"
            style={{ background: "rgba(245,240,232,0.1)", border: "none", borderRadius: 999, padding: 8,
                     color: "#F5F0E8", cursor: "pointer", display: "inline-flex" }}>
      <ArrowLeft size={18} aria-hidden="true" />
    </button>
  );
}

function ShareCard({ url }) {
  return (
    <ChalkCard data-testid="tournament-share" style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", fontWeight: 700 }}>
        Public link
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
        <code style={{ flex: 1, fontSize: 11, color: "var(--chalk-board)", background: "#FBF8F2", padding: 8, borderRadius: 6,
                       overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{url}</code>
        <button data-testid="tournament-copy-link" aria-label="Copy link"
                onClick={async () => { const ok = await copyToClipboard(url); toast[ok ? "success" : "error"](ok ? "Copied." : "Copy failed."); }}
                style={{ padding: "8px 10px", borderRadius: 8, border: "0.5px solid var(--chalk-border)",
                         background: "transparent", color: "var(--green-mid)", cursor: "pointer" }}>
          <Copy size={16} aria-hidden="true" />
        </button>
      </div>
    </ChalkCard>
  );
}

function TabBar({ tabs, tab, setTab, hub }) {
  return (
    <div style={{ display: "flex", gap: 4, marginBottom: 12, overflowX: "auto", paddingBottom: 2 }} data-testid="tournament-tabs">
      {tabs.map(({ k, label, icon: Icon, hub: needsHub }) => (
        <button key={k} onClick={() => setTab(k)} data-testid={`tournament-tab-${k}`}
                style={{
                  flex: "0 0 auto", padding: "8px 12px", borderRadius: 8,
                  background: tab === k ? "var(--chalk-board)" : "transparent",
                  color: tab === k ? "#F5F0E8" : "var(--chalk-board)",
                  border: "0.5px solid var(--chalk-border)", cursor: "pointer", fontSize: 12, fontWeight: 700,
                  display: "inline-flex", alignItems: "center", gap: 4, opacity: needsHub && !hub ? 0.6 : 1,
                  transition: "background-color 150ms ease, color 150ms ease",
                }}>
          <Icon size={14} aria-hidden="true" /> {label}
        </button>
      ))}
    </div>
  );
}
